/** @jsxImportSource jsx-dom */

import { selectAll } from "@fruugo/utilities";
import { getCountryLanguages } from "./api";
import { i18n, initTranslation, waitForTranslations } from "./i18n";
import { CountryLanguageResponse } from "../types";
import {
  closeModal,
  getModalById,
  openModal,
  updateModalBody,
} from "./components/modal/Modal";
import { ModalType } from "./components/modal/types";
import {
  LocalePickerFormControls,
  LocalePickerModalContent,
} from "./components/modal/locale-picker/LocalePickerModalContent";
import { LocalePickerBasketWarning } from "./components/modal/locale-picker/LocalePickerBasketWarning";
import {
  LocaleSelection,
  findCountry,
  translate,
} from "./components/modal/locale-picker/shared";
import {
  sendClickEvent,
  sendViewContentEvent,
} from "./analytics/google-analytics";

const triggerSelector = ".js-open-locale-picker";

// The most recent language picked in the modal, so slower responses for an earlier pick are ignored
let requestedLanguage: string | undefined;

// Country and language data per display language, kept for this page only. It includes content-hashed messages
// URLs, which go stale after a deploy, so it isn't persisted across pages (the endpoint's 5 minute Cache-Control
// covers that).
const countryLanguagesCache = new Map<
  string,
  Promise<CountryLanguageResponse>
>();

export function initLocalePicker() {
  selectAll<HTMLElement>(triggerSelector)?.forEach((trigger) =>
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      openLocalePicker();
    })
  );
}

async function openLocalePicker() {
  // Picker labels fall back to their keys if translations never arrive, but don't block opening on it
  await waitForTranslations().catch(() => undefined);

  const selection: LocaleSelection = {
    country: window.currentCountry,
    language: window.currentLang,
  };

  openModal(
    ModalType.LocalePicker,
    <img
      class="tw:w-8 tw:h-8 tw:m-auto tw:my-8 tw:block"
      width="50"
      height="50"
      src="/marketplace/images/loader.svg"
      alt={i18n("common.loading")}
    />,
    {
      ariaLabel: i18n("localepicker.title"),
      size: "sm",
      onOpen: () =>
        sendViewContentEvent("locale_picker_modal", "Locale Picker Modal"),
    }
  );

  // The modal may have been left in another language last time it was open
  requestedLanguage = selection.language;
  setModalLanguage(selection.language, window.rtl);

  try {
    const data = await loadCountryLanguages(selection.language);
    showPicker(data, selection);
  } catch (error) {
    updateModalBody(
      ModalType.LocalePicker,
      <p class="tw:p-4 tw:m-0" role="alert">
        {i18n("localepicker.error")}
      </p>
    );
  }
}

function showPicker(data: CountryLanguageResponse, selection: LocaleSelection) {
  updateModalBody(
    ModalType.LocalePicker,
    <LocalePickerModalContent
      data={data}
      selection={selection}
      onUpdate={(newSelection) => handleUpdate(data, newSelection)}
      onLanguageChange={(language, form) =>
        handleLanguageChange(data, language, form)
      }
    />
  );
}

// Re-render only the modal in the chosen language, the rest of the page stays as it is until Update
async function handleLanguageChange(
  data: CountryLanguageResponse,
  languageCode: string,
  { startLoading, getSelection }: LocalePickerFormControls
) {
  requestedLanguage = languageCode;
  const stopLoading = startLoading();

  const language = data.languages.find(
    (option) => option.language === languageCode
  );

  const [newData] = await Promise.all([
    loadCountryLanguages(languageCode).catch(() => data),
    language?.messagesUrl
      ? initTranslation(language.messagesUrl)
      : Promise.resolve({}),
  ]);

  // A newer language pick is still loading and will replace the form when it lands
  if (requestedLanguage !== languageCode) return;

  stopLoading();
  setModalLanguage(languageCode, Boolean(language?.rightToLeft));
  // Read the selection now rather than when the language was picked, so a country chosen while it loaded is kept
  showPicker(newData, { ...getSelection(), language: languageCode });
  document.querySelector<HTMLSelectElement>("#locale-picker-language")?.focus();
}

function setModalLanguage(language: string, rightToLeft: boolean) {
  const modal = getModalById(ModalType.LocalePicker);
  if (!modal) return;

  modal.setAttribute("lang", language);
  modal.setAttribute("dir", rightToLeft ? "rtl" : "ltr");

  // The visible title is part of the body and re-renders with it, but the dialog's accessible name needs updating
  modal.setAttribute("aria-label", translate("localepicker.title", language));
}

function handleUpdate(
  data: CountryLanguageResponse,
  selection: LocaleSelection
) {
  const countryChanged = selection.country !== window.currentCountry;
  const languageChanged = selection.language !== window.currentLang;

  if (!countryChanged && !languageChanged) {
    closeModal();
    return;
  }

  // Baskets are held per domain, so warn before leaving one with items in it
  if (countryChanged && window.totalCartItems > 0) {
    const currentCountryName =
      findCountry(data, window.currentCountry)?.localisedDisplayName ??
      window.currentCountry;
    const targetCountryName =
      findCountry(data, selection.country)?.localisedDisplayName ??
      selection.country;

    updateModalBody(
      ModalType.LocalePicker,
      <LocalePickerBasketWarning
        language={selection.language}
        currentCountryName={currentCountryName}
        targetCountryName={targetCountryName}
        onContinue={() => changeLocale(selection)}
        onCancel={() => showPicker(data, selection)}
      />
    );
    focusFirstButton();
    return;
  }

  changeLocale(selection);
}

function changeLocale({ country, language }: LocaleSelection) {
  const redirectUrl = buildRedirectUrl(country, language);

  // GA4 sends events with sendBeacon, so this still reaches GA as the page unloads
  sendClickEvent("locale_picker_update", `${country}|${language}`, redirectUrl);

  closeModal();
  window.location.assign(redirectUrl);
}

// The redirect endpoint rebuilds the equivalent page on the target domain in the target language
function buildRedirectUrl(country: string, language: string) {
  const url =
    window.location.origin + window.location.pathname + window.location.search;
  return `/marketplace/api/redirect?url=${encodeURIComponent(
    url
  )}&country=${country}&language=${language}`;
}

function focusFirstButton() {
  document
    .querySelector<HTMLButtonElement>(
      `[data-modal-type="${ModalType.LocalePicker}"] .js-modal-body button`
    )
    ?.focus();
}

function loadCountryLanguages(
  language: string
): Promise<CountryLanguageResponse> {
  let request = countryLanguagesCache.get(language);
  if (!request) {
    request = getCountryLanguages(language);
    countryLanguagesCache.set(language, request);
    // Don't keep a failed request, so the next attempt can retry
    request.catch(() => countryLanguagesCache.delete(language));
  }
  return request;
}
